import React from 'react';

import { Badge, Box, Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { numberToOrdinalFormatter } from 'utils/formatters';

export interface EventCardInfo {
  eventName: string;
  coachName: string;
  date: string;
  startTime: string;
  duration: number;
}

interface IModalDragNotificationConfirmationProps {
  opened: boolean;
  oldEvent: EventCardInfo | null;
  newEvent: EventCardInfo | null;
  onCancel: () => void;
  onDontSend: () => void;
  onSend: () => void;
}

const formatDate = (date: string): string => {
  const d = dayjs(date);
  const day = d.date();
  return `${d.format('dddd, MMMM')} ${day}${numberToOrdinalFormatter(day)} ${d.format('YYYY')}`;
};

const formatTimeRange = (date: string, startTime: string, duration: number): string => {
  const start = dayjs(`${date}T${startTime}`);
  const end = start.add(duration, 'minute');
  return `${start.format('h:mmA')} - ${end.format('h:mmA')}`;
};

const EventCard = ({ info, isOld }: { info: EventCardInfo; isOld: boolean }): JSX.Element => {
  const textColor = isOld ? ('dimmed' as const) : undefined;
  return (
    <Box
      sx={{
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        padding: '0.875rem 1.25rem',
      }}
    >
      <Group position='apart' align='center' noWrap>
        <Text fw={700} size='sm' color={textColor}>
          {info.eventName}
        </Text>
        <Badge color={isOld ? 'gray' : 'green'} variant='filled' size='sm'>
          {isOld ? 'Old Event' : 'New Event'}
        </Badge>
      </Group>
      <Text size='sm' color={textColor}>
        {info.coachName}
      </Text>
      <Group position='apart' noWrap>
        <Text size='sm' color={textColor}>
          {formatDate(info.date)}
        </Text>
        <Text size='sm' color={textColor}>
          {formatTimeRange(info.date, info.startTime, info.duration)}
        </Text>
      </Group>
    </Box>
  );
};

const ModalDragNotificationConfirmation = ({
  opened,
  oldEvent,
  newEvent,
  onCancel,
  onDontSend,
  onSend,
}: IModalDragNotificationConfirmationProps): JSX.Element => (
  <Modal
    centered
    opened={opened}
    onClose={onCancel}
    title={<></>}
    size={600}
    styles={{
      header: { position: 'relative', minHeight: 48, padding: '0.75rem 1rem 0' },
      close: {
        position: 'absolute',
        top: '0.75rem',
        right: '0.75rem',
        '& svg': { width: 20, height: 20, strokeWidth: 2, color: 'black' },
      },
      body: { padding: '0 2.5rem 2.5rem' },
    }}
  >
    <Stack spacing='xl'>
      <Title order={3} fw={700} align='center'>
        Would you like to send an update to athletes who have this event reserved?
      </Title>
      {oldEvent && newEvent && (
        <Stack spacing='md' mx={40}>
          <EventCard info={oldEvent} isOld />
          <EventCard info={newEvent} isOld={false} />
        </Stack>
      )}
      <Group position='center'>
        <Button variant='subtle' color='gray' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='outline' onClick={onDontSend}>
          Don&apos;t Send
        </Button>
        <Button color='kiloBlue' onClick={onSend}>
          Send
        </Button>
      </Group>
    </Stack>
  </Modal>
);

export default ModalDragNotificationConfirmation;
